import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardAPI } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Calendar, 
  AlertTriangle, 
  MapPin, 
  ChevronRight,
  ChevronLeft,
  Loader2
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const WeekAtAGlance = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);

  useEffect(() => {
    loadWeekData();
  }, [weekOffset]);

  const loadWeekData = async () => {
    setLoading(true);
    try {
      const response = await dashboardAPI.weekAtAGlance(weekOffset);
      setData(response.data);
      // Auto-select today if viewing current week, otherwise first day
      const todayData = response.data.days.find(d => d.is_today);
      if (todayData) {
        setSelectedDay(todayData);
      } else {
        setSelectedDay(response.data.days[0]);
      }
    } catch (error) {
      console.error('Failed to load week data:', error);
    } finally {
      setLoading(false);
    }
  };

  const goToPreviousWeek = () => setWeekOffset(prev => prev - 1);
  const goToNextWeek = () => setWeekOffset(prev => prev + 1);
  const goToCurrentWeek = () => setWeekOffset(0);

  const formatDateRange = (start, end) => {
    if (!start) return '';
    try {
      const startDate = parseISO(start);
      const endDate = end ? parseISO(end) : startDate;
      if (start === end || !end) {
        return format(startDate, 'MMM d');
      }
      return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d')}`;
    } catch {
      return start;
    }
  };

  if (loading) {
    return (
      <Card className="border-border/50 bg-card/50">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return null;
  }

  const { days, needs_attention } = data;
  const totalAttention = (needs_attention?.tentative_events || 0);

  return (
    <Card className="border-border/50 bg-card/50" data-testid="week-at-a-glance">
      {/* Header */}
      <div className="px-6 pt-6 pb-4 border-b border-border/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <Calendar className="h-5 w-5 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold">
                {weekOffset === 0 ? 'This Week at a Glance' : 
                 weekOffset === -1 ? 'Last Week' : 
                 weekOffset === 1 ? 'Next Week' : 
                 weekOffset < 0 ? `${Math.abs(weekOffset)} Weeks Ago` : 
                 `${weekOffset} Weeks Ahead`}
              </h2>
              <p className="text-sm text-muted-foreground">
                {format(parseISO(data.week_start), 'MMM d')} - {format(parseISO(data.week_end), 'MMM d, yyyy')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="icon"
              onClick={goToPreviousWeek}
              data-testid="prev-week-btn"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {weekOffset !== 0 && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={goToCurrentWeek}
                data-testid="current-week-btn"
              >
                Today
              </Button>
            )}
            <Button 
              variant="outline" 
              size="icon"
              onClick={goToNextWeek}
              data-testid="next-week-btn"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Week Strip */}
      <div className="px-6 py-4 border-b border-border/50">
        <div className="grid grid-cols-7 gap-2">
          {days.map((day) => (
            <button
              key={day.date}
              onClick={() => setSelectedDay(day)}
              className={`relative p-3 rounded-lg text-center transition-all ${
                selectedDay?.date === day.date
                  ? 'bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2 ring-offset-background'
                  : day.is_today
                  ? 'bg-emerald-500/20 hover:bg-emerald-500/30'
                  : 'bg-background/50 hover:bg-background/80'
              }`}
              data-testid={`day-${day.date}`}
            >
              <div className="text-xs font-medium opacity-70">{day.day_name}</div>
              <div className={`text-2xl font-bold ${day.is_today && selectedDay?.date !== day.date ? 'text-emerald-400' : ''}`}>
                {day.day_num}
              </div>
              {day.is_today && selectedDay?.date !== day.date && (
                <div className="text-[10px] uppercase tracking-wider text-emerald-400 font-semibold">Today</div>
              )}
              <div className="flex items-center justify-center gap-1 mt-1">
                {day.event_count > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    selectedDay?.date === day.date 
                      ? 'bg-primary-foreground/20 text-primary-foreground' 
                      : 'bg-emerald-500/20 text-emerald-400'
                  }`}>
                    {day.event_count}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Selected Day Details */}
      <CardContent className="p-6">
        {selectedDay && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              {selectedDay.is_today ? (
                <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Today</Badge>
              ) : null}
              {format(parseISO(selectedDay.date), 'EEEE, MMMM d')}
            </h3>

            <div>
              {/* Events */}
              <div>
                <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Events ({selectedDay.event_count})
                </h4>
                {selectedDay.events.length > 0 ? (
                  <div className="space-y-2">
                    {selectedDay.events.map((event) => (
                      <Link
                        key={event.id}
                        to={`/events/${event.id}`}
                        className="block p-3 rounded-lg bg-background/50 hover:bg-background/80 transition-colors group"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate group-hover:text-primary transition-colors">
                              {event.event_name}
                            </p>
                            {event.flex_number && (
                              <p className="text-xs text-muted-foreground font-mono">#{event.flex_number}</p>
                            )}
                            {event.location_name && (
                              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                <MapPin className="h-3 w-3" />
                                {event.location_name}
                              </p>
                            )}
                          </div>
                          <Badge 
                            variant="outline" 
                            className={`text-xs shrink-0 ml-2 ${
                              event.status === 'confirmed' 
                                ? 'border-emerald-500/30 text-emerald-400' 
                                : event.status === 'tentative'
                                ? 'border-yellow-500/30 text-yellow-400'
                                : 'border-border'
                            }`}
                          >
                            {event.status}
                          </Badge>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-muted-foreground bg-background/30 rounded-lg">
                    <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No events scheduled</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>

      {/* Needs Attention Footer */}
      {totalAttention > 0 && (
        <div className="px-6 py-4 border-t border-border/50 bg-orange-500/5">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-orange-400" />
            <div className="flex-1">
              <span className="text-sm font-medium text-orange-400">Needs Attention: </span>
              <span className="text-sm text-muted-foreground">
                {needs_attention.tentative_events > 0 && (
                  <Link to="/events" className="hover:underline">
                    {needs_attention.tentative_events} tentative event{needs_attention.tentative_events !== 1 ? 's' : ''}
                  </Link>
                )}
              </span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
      )}
    </Card>
  );
};

export default WeekAtAGlance;
